import { AddOnType } from "@/models/Subscriptions/AddOns/AddOnType";

export type AddonOption = {
  value: AddOnType;
  label: string;
  icon: string;
  description: string;
}

export type AddonValue = AddOnType | string;

export type GiftOption = {
  value: string;
  label: string;
  icon: string;
  description: string;
}

export type OfferOptionConfiguration = {
  title: string;
  content: string;
  bullets: string[];
  couponCode: string;
  couponDescription?: string;
  originalPrice?: number | null;
  currentPayingAmount?: number | null;
  discountedPrice?: number | null;
  billingPeriod?: string;
  canClaim?: boolean;
  discountPercent?: number | null;
  claimStatus?: string;
  claimMessage?: string;
  hasActiveRetentionOffer?: boolean;
  historicalClaimedPrices?: number[];
  availableAddons: AddonValue[];
  giftCount: number;
  selectedGiftSlots: Array<string | null>;
  selectedGiftDurations?: string[];
  maxClaimsPerRestaurantForThisOffer?: number;
  validityPeriodUnit?: "month" | "year";
  validityPeriodCount?: number;
  minimumFinalPrice?: number | null;
}

export type OfferPricingConfiguration = {
  originalPrice?: number | null;
  currentPayingAmount?: number | null;
  discountedPrice?: number | null;
  billingPeriod?: string;
  couponDescription?: string;
  discountPercent?: number | null;
  canClaim?: boolean;
  claimStatus?: string;
  claimMessage?: string;
  hasActiveRetentionOffer?: boolean;
  historicalClaimedPrices?: number[];
}

export type CardConfiguration = {
  id: string;
  adminTitle: string;
  previewTag: string;
  theme: string;
  reasonLabels: string[];
  reasonSummary: string;
  headline: string;
  subheader: string;
  content: string;
  bullets: string[];
  recommendedOffer: OfferOptionConfiguration;
  discountOffer: OfferOptionConfiguration;
  couponCode: string;
  giftCount: number;
  selectedGiftSlots: Array<string | null>;
  selectedGiftDurations?: string[];
}

export type PackageConfiguration = {
  id: number;
  packageName: string;
  availableAddons: AddonValue[];
  cardConfigurations: CardConfiguration[];
  updatedOn?: string;
}

type PersistedOfferOptionConfiguration =
  Partial<OfferOptionConfiguration> & {
    pricing?: OfferPricingConfiguration | null;
  };

type PersistedCardConfiguration = Omit<Partial<CardConfiguration>, "recommendedOffer" | "discountOffer"> & {
  recommendedOffer?: PersistedOfferOptionConfiguration;
  discountOffer?: PersistedOfferOptionConfiguration;
};

export type PersistedPackageConfiguration = Omit<Partial<PackageConfiguration>, "cardConfigurations"> & {
  personalizedOffer?: PersistedOfferOptionConfiguration;
  cardConfigurations?: PersistedCardConfiguration[];
};

const clonePackageConfiguration = (configuration: PackageConfiguration): PackageConfiguration =>
  JSON.parse(JSON.stringify(configuration)) as PackageConfiguration;

const normalizeGiftSlots = (selectedGiftSlots?: Array<string | null>): Array<string | null> => {
  const slots = Array.isArray(selectedGiftSlots) ? [...selectedGiftSlots] : [];

  if (slots.length < 3) {
    slots.push(...Array.from({ length: 3 - slots.length }, (): null => null));
  }

  return slots.slice(0, 3);
};

const normalizeGiftDurations = (selectedGiftDurations?: string[]): string[] => {
  const durations = Array.isArray(selectedGiftDurations) ? [...selectedGiftDurations] : [];

  if (durations.length < 3) {
    durations.push(...Array.from({ length: 3 - durations.length }, () => ""));
  }

  return durations.slice(0, 3);
};

const mergeOfferOptionConfiguration = (
  baseOption: OfferOptionConfiguration,
  persistedOption?: PersistedOfferOptionConfiguration | null
): OfferOptionConfiguration => ({
  ...baseOption,
  couponCode: persistedOption?.couponCode ?? baseOption.couponCode,
  couponDescription:
    persistedOption?.pricing?.couponDescription
    ?? persistedOption?.couponDescription
    ?? baseOption.couponDescription,
  originalPrice:
    typeof persistedOption?.pricing?.originalPrice === "number"
      ? persistedOption.pricing.originalPrice
      : typeof persistedOption?.originalPrice === "number"
        ? persistedOption.originalPrice
        : (baseOption.originalPrice ?? null),
  currentPayingAmount:
    typeof persistedOption?.pricing?.currentPayingAmount === "number"
      ? persistedOption.pricing.currentPayingAmount
      : typeof persistedOption?.currentPayingAmount === "number"
        ? persistedOption.currentPayingAmount
        : (baseOption.currentPayingAmount ?? null),
  discountedPrice:
    typeof persistedOption?.pricing?.discountedPrice === "number"
      ? persistedOption.pricing.discountedPrice
      : typeof persistedOption?.originalPrice === "number" && persistedOption.discountedPrice === null
        ? null
        : typeof persistedOption?.discountedPrice === "number"
          ? persistedOption.discountedPrice
          : (baseOption.discountedPrice ?? null),
  billingPeriod:
    persistedOption?.pricing?.billingPeriod
    ?? persistedOption?.billingPeriod
    ?? baseOption.billingPeriod,
  canClaim:
    typeof persistedOption?.pricing?.canClaim === "boolean"
      ? persistedOption.pricing.canClaim
      : typeof persistedOption?.canClaim === "boolean"
        ? persistedOption.canClaim
        : (baseOption.canClaim ?? true),
  discountPercent:
    typeof persistedOption?.pricing?.discountPercent === "number"
      ? persistedOption.pricing.discountPercent
      : typeof persistedOption?.discountPercent === "number"
        ? persistedOption.discountPercent
        : (baseOption.discountPercent ?? null),
  claimStatus:
    persistedOption?.pricing?.claimStatus
    ?? persistedOption?.claimStatus
    ?? baseOption.claimStatus,
  claimMessage:
    persistedOption?.pricing?.claimMessage
    ?? persistedOption?.claimMessage
    ?? baseOption.claimMessage,
  hasActiveRetentionOffer:
    typeof persistedOption?.pricing?.hasActiveRetentionOffer === "boolean"
      ? persistedOption.pricing.hasActiveRetentionOffer
      : typeof persistedOption?.hasActiveRetentionOffer === "boolean"
        ? persistedOption.hasActiveRetentionOffer
        : (baseOption.hasActiveRetentionOffer ?? false),
  historicalClaimedPrices: Array.isArray(persistedOption?.pricing?.historicalClaimedPrices)
    ? persistedOption.pricing.historicalClaimedPrices.filter((price): price is number => typeof price === "number")
    : Array.isArray(persistedOption?.historicalClaimedPrices)
      ? persistedOption.historicalClaimedPrices.filter((price): price is number => typeof price === "number")
      : (baseOption.historicalClaimedPrices ?? []),
  availableAddons: Array.isArray(persistedOption?.availableAddons)
    ? [...persistedOption!.availableAddons]
    : [...baseOption.availableAddons],
  giftCount: typeof persistedOption?.giftCount === "number" ? persistedOption.giftCount : baseOption.giftCount,
  selectedGiftSlots: normalizeGiftSlots(persistedOption?.selectedGiftSlots),
  selectedGiftDurations: normalizeGiftDurations(persistedOption?.selectedGiftDurations || baseOption.selectedGiftDurations),
  maxClaimsPerRestaurantForThisOffer:
    typeof persistedOption?.maxClaimsPerRestaurantForThisOffer === "number"
      ? persistedOption.maxClaimsPerRestaurantForThisOffer
      : (baseOption.maxClaimsPerRestaurantForThisOffer ?? 1),
  validityPeriodUnit:
    persistedOption?.validityPeriodUnit === "year"
      ? "year"
      : persistedOption?.validityPeriodUnit === "month"
        ? "month"
        : (baseOption.validityPeriodUnit ?? "month"),
  validityPeriodCount:
    typeof persistedOption?.validityPeriodCount === "number"
      ? persistedOption.validityPeriodCount
      : (baseOption.validityPeriodCount ?? 1),
  minimumFinalPrice:
    typeof persistedOption?.minimumFinalPrice === "number"
      ? persistedOption.minimumFinalPrice
      : (baseOption.minimumFinalPrice ?? null),
});

const mergeCardConfiguration = (
  baseCard: CardConfiguration,
  persistedCard?: PersistedCardConfiguration | null
): CardConfiguration => ({
  ...baseCard,
  couponCode: persistedCard?.couponCode ?? baseCard.couponCode,
  giftCount: typeof persistedCard?.giftCount === "number" ? persistedCard.giftCount : baseCard.giftCount,
  selectedGiftSlots: normalizeGiftSlots(persistedCard?.selectedGiftSlots),
  selectedGiftDurations: normalizeGiftDurations(persistedCard?.selectedGiftDurations || baseCard.selectedGiftDurations),
  recommendedOffer: mergeOfferOptionConfiguration(baseCard.recommendedOffer, persistedCard?.recommendedOffer),
  discountOffer: mergeOfferOptionConfiguration(baseCard.discountOffer, persistedCard?.discountOffer),
});

const toPersistedOfferOptionConfiguration = (
  option: OfferOptionConfiguration
): PersistedOfferOptionConfiguration => ({
  couponCode: option.couponCode,
  availableAddons: [...option.availableAddons],
  giftCount: option.giftCount,
  selectedGiftSlots: normalizeGiftSlots(option.selectedGiftSlots),
  selectedGiftDurations: normalizeGiftDurations(option.selectedGiftDurations),
  pricing: {
    originalPrice: option.originalPrice ?? null,
    currentPayingAmount: option.currentPayingAmount ?? null,
    discountedPrice: option.discountedPrice ?? null,
    billingPeriod: option.billingPeriod ?? "",
    couponDescription: option.couponDescription ?? "",
    canClaim: option.canClaim ?? true,
    discountPercent: option.discountPercent ?? null,
    claimStatus: option.claimStatus ?? "",
    claimMessage: option.claimMessage ?? "",
    hasActiveRetentionOffer: option.hasActiveRetentionOffer ?? false,
    historicalClaimedPrices: Array.isArray(option.historicalClaimedPrices) ? [...option.historicalClaimedPrices] : [],
  },
  maxClaimsPerRestaurantForThisOffer: option.maxClaimsPerRestaurantForThisOffer ?? 1,
  validityPeriodUnit: option.validityPeriodUnit ?? "month",
  validityPeriodCount: option.validityPeriodCount ?? 1,
  minimumFinalPrice: option.minimumFinalPrice ?? null,
});

const toPersistedCardConfiguration = (card: CardConfiguration): PersistedCardConfiguration => ({
  id: card.id,
  recommendedOffer: toPersistedOfferOptionConfiguration(card.recommendedOffer),
  discountOffer: toPersistedOfferOptionConfiguration(card.discountOffer),
});

export const createPackageCancellationWorkflowConfiguration = (
  packageId: number,
  packageName: string
): PackageConfiguration => {
  const normalizedName = packageName.toLowerCase();
  const templates = createDefaultCancellationWorkflowConfigurations();

  let template = templates[2];
  if (normalizedName.includes("starter")) {
    template = templates[0];
  } else if (normalizedName.includes("standard")) {
    template = templates[1];
  }

  const configuration = clonePackageConfiguration(template);
  configuration.id = packageId;
  configuration.packageName = packageName;
  return configuration;
};

const createEmptyOfferOptionConfiguration = (
  option: OfferOptionConfiguration
): OfferOptionConfiguration => {
  const selectedGiftSlots: Array<string | null> = [null, null, null];
  const selectedGiftDurations: string[] = ["", "", ""];

  return {
    ...option,
    couponCode: "",
    couponDescription: "",
    availableAddons: [],
    giftCount: 0,
    selectedGiftSlots,
    selectedGiftDurations,
    maxClaimsPerRestaurantForThisOffer: option.maxClaimsPerRestaurantForThisOffer ?? 1,
    validityPeriodUnit: option.validityPeriodUnit ?? "month",
    validityPeriodCount: option.validityPeriodCount ?? 1,
    minimumFinalPrice: option.minimumFinalPrice ?? null,
  };
};

export const createEmptyPackageCancellationWorkflowConfiguration = (
  packageId: number,
  packageName: string
): PackageConfiguration => {
  const configuration = createPackageCancellationWorkflowConfiguration(packageId, packageName);

  const selectedGiftSlots: Array<string | null> = [null, null, null];
  const selectedGiftDurations: string[] = ["", "", ""];

  return {
    ...configuration,
    availableAddons: [],
    cardConfigurations: configuration.cardConfigurations.map((cardConfiguration) => ({
      ...cardConfiguration,
      couponCode: "",
      giftCount: 0,
      selectedGiftSlots: [...selectedGiftSlots],
      selectedGiftDurations: [...selectedGiftDurations],
      recommendedOffer: createEmptyOfferOptionConfiguration(cardConfiguration.recommendedOffer),
      discountOffer: createEmptyOfferOptionConfiguration(cardConfiguration.discountOffer),
    })),
  };
};

export type CancellationWorkflowConfigurationDto = {
  packageId: number;
  packageName: string;
  configuration: PersistedPackageConfiguration | null;
  isActive: boolean;
  updatedOn: string;
}

export type SaveCancellationWorkflowConfigurationsDto = {
  configurations: CancellationWorkflowConfigurationDto[];
}

export type GetCancellationWorkflowConfigurationDto = {
  subscriptionPackageId: number;
  restaurantId?: number | null;
}

export type CouponRetentionRuleDto = {
  couponCode: string;
  maxClaimsPerRestaurantForThisOffer: number;
  validityPeriodUnit: "month" | "year";
  validityPeriodCount: number;
  minimumFinalPrice: number | null;
  isActive: boolean;
  updatedOn?: string | null;
}

export type GetCouponRetentionRuleDto = {
  couponCode: string;
}

export type SaveCouponRetentionRuleDto = {
  rule: CouponRetentionRuleDto;
}

export type TrackRetentionAttemptDto = {
  restaurantId: number;
  userId: number;
  subscriptionPackageId: number;
  cancellationReason: string;
  workflowCardId: string;
  status: string;
  metadataJson: string;
}

export type ClaimRetentionOfferDto = {
  restaurantId: number;
  userId: number;
  subscriptionPackageId: number;
  cancellationReason: string;
  selectedAddonJson: string;
}

export type SubmitFeatureRequestDto = {
  restaurantId: number;
  userId: number;
  subscriptionPackageId: number;
  cancellationReason: string;
  name: string;
  email: string;
  details: string;
}

export type SubmitSupportCallRequestDto = {
  restaurantId: number;
  userId: number;
  subscriptionPackageId: number;
  cancellationReason: string;
  name: string;
  email: string;
  phone: string;
  availableDate: string;
  details: string;
}

export type SubmitCancellationFeedbackDto = {
  restaurantId: number;
  userId: number;
  subscriptionPackageId: number;
  cancellationReason: string;
  name: string;
  email: string;
  feedback: string;
}

export type RetentionEmailSubmissionResultDto = {
  accepted: boolean;
  correlationId: string;
  subject: string;
  environment: string;
  acceptedRecipients: string[];
  diagnosticsLogPath: string;
  failureLogPath?: string | null;
  failureMessage?: string | null;
}

export type ClaimRetentionOfferResultDto = {
  claimed: boolean;
  claimStatus: string;
  couponCode: string;
  message: string;
  couponId?: number | null;
  retentionOfferClaimId: number;
  discountedNextPaymentAmount?: number | null;
  remainingRenewalCount: number;
  appliesFromNextPayment: boolean;
}

export type RetentionReportEntryDto = {
  id: number;
  activityOn: string;
  companyName: string;
  customerName: string;
  customerEmail: string;
  packageName: string;
  issueType: string;
  couponCode: string;
  outcomeStatus: string;
  workflowCardId: string;
}

export const defaultAddonOptions: AddonOption[] = [
  {
    value: AddOnType.AllergenAI,
    label: "Allergen AI Addon",
    icon: "fa fa-magic",
    description: "Offer 6 months of Allergen AI access.",
  },
  {
    value: AddOnType.RecipeCost,
    label: "Recipe Cost Addon",
    icon: "fa fa-calculator",
    description: "Unlock Recipe Cost without extra charge.",
  },
  {
    value: AddOnType.SugarAlcohols,
    label: "Sugar Alcohol Addon",
    icon: "fa fa-cube",
    description: "Include Sugar Alcohol tools for the retention period.",
  },
  {
    value: AddOnType.WellnessModule,
    label: "Wellness Analyzer Addon",
    icon: "fa fa-heartbeat",
    description: "Include Wellness Analyzer for the retention period.",
  },
];

export const defaultGiftOptions: GiftOption[] = [
  {
    value: "free-addon",
    label: "1 FREE ADDON",
    icon: "fa fa-gift",
    description: "User will click the gift and then choose from available add-ons.",
  },
  {
    value: "additional-15-discount",
    label: "Additional 15% discount",
    icon: "fa fa-percent",
    description: "Stack an extra 15% discount onto the main retention offer.",
  },
  {
    value: "priority-support",
    label: "Priority Support",
    icon: "fa fa-life-ring",
    description: "Move the customer to a faster support lane as a retention gift.",
  },
  {
    value: "free-month-extension",
    label: "Free month extension",
    icon: "fa fa-calendar-plus-o",
    description: "Extend the subscription by one additional month at no charge.",
  },
];

const createPersonalizedCardConfiguration = (
  availableAddons: AddOnType[]
): CardConfiguration => ({
  id: "personalized-offer",
  adminTitle: "Personalized offer",
  previewTag: "Personalized offer",
  theme: "discount",
  reasonLabels: [
    "Too expensive",
    "No longer needed",
    "Switching service",
    "Label accuracy",
    "Missing features",
    "Customer support",
    "Other",
  ],
  reasonSummary: "Single frontend-managed personalized offer used for every cancellation path.",
  headline: "Before you cancel, here is a personalized option.",
  subheader: "Keep your current plan active with a simpler retention offer and a few extra perks.",
  content: "Keep your workspace active with a personalized retention option and extra perks before you decide to leave.",
  bullets: [
    "Keep your account active",
    "Apply the discount from the next payment only",
    "Unbox extra perks before you decide",
  ],
  recommendedOffer: {
    title: "Current package",
    content: "Keep your current package active with a personalized retention offer and extra perks.",
    bullets: [
      "Stay on your current package",
      "Applies from the next payment",
      "Add extra perks before you decide",
    ],
    couponCode: "",
    couponDescription: "",
    originalPrice: null,
    discountedPrice: null,
    billingPeriod: "month",
    availableAddons: [...availableAddons],
    giftCount: 2,
    selectedGiftSlots: [null, null, null],
    selectedGiftDurations: ["", "", ""],
    maxClaimsPerRestaurantForThisOffer: 1,
    validityPeriodUnit: "month",
    validityPeriodCount: 6,
    minimumFinalPrice: null,
  },
  discountOffer: {
    title: "Current package",
    content: "Keep your current package active with a personalized retention offer and extra perks.",
    bullets: [
      "Stay on your current package",
      "Keep your current workflow moving",
      "Add extra perks before you decide",
    ],
    couponCode: "",
    couponDescription: "",
    originalPrice: null,
    discountedPrice: null,
    billingPeriod: "month",
    availableAddons: [...availableAddons],
    giftCount: 2,
    selectedGiftSlots: [null, null, null],
    selectedGiftDurations: ["", "", ""],
    maxClaimsPerRestaurantForThisOffer: 1,
    validityPeriodUnit: "month",
    validityPeriodCount: 6,
    minimumFinalPrice: null,
  },
  couponCode: "",
  giftCount: 0,
  selectedGiftSlots: [null, null, null],
  selectedGiftDurations: ["", "", ""],
});

export const createDefaultCancellationWorkflowConfigurations = (): PackageConfiguration[] => [
  {
    id: 1,
    packageName: "Starter",
    availableAddons: [AddOnType.AllergenAI],
    cardConfigurations: [
      createPersonalizedCardConfiguration([AddOnType.AllergenAI]),
    ],
  },
  {
    id: 2,
    packageName: "Standard",
    availableAddons: [AddOnType.AllergenAI, AddOnType.RecipeCost, AddOnType.WellnessModule],
    cardConfigurations: [
      createPersonalizedCardConfiguration(
        [AddOnType.AllergenAI, AddOnType.RecipeCost, AddOnType.WellnessModule]
      ),
    ],
  },
  {
    id: 3,
    packageName: "Premium",
    availableAddons: [AddOnType.AllergenAI, AddOnType.RecipeCost, AddOnType.WellnessModule],
    cardConfigurations: [
      createPersonalizedCardConfiguration(
        [AddOnType.AllergenAI, AddOnType.RecipeCost, AddOnType.WellnessModule]
      ),
    ],
  },
];

export const serializeWorkflowConfigurations = (configurations: PackageConfiguration[]): CancellationWorkflowConfigurationDto[] =>
  configurations.map((configuration) => ({
    packageId: configuration.id,
    packageName: configuration.packageName,
    configuration: {
      id: configuration.id,
      packageName: configuration.packageName,
      availableAddons: [...configuration.availableAddons],
      personalizedOffer: toPersistedOfferOptionConfiguration(
        configuration.cardConfigurations[0]?.recommendedOffer || createPackageCancellationWorkflowConfiguration(configuration.id, configuration.packageName).cardConfigurations[0].recommendedOffer
      ),
    },
    isActive: true,
    updatedOn: configuration.updatedOn || new Date().toISOString(),
  }));

export const deserializeWorkflowConfigurations = (
  configurations: CancellationWorkflowConfigurationDto[]
): PackageConfiguration[] =>
  configurations.map((configuration) => ({
    ...resolveWorkflowConfiguration(configuration.configuration, configuration.packageId, configuration.packageName),
    updatedOn: configuration.updatedOn,
  }));

export const resolveWorkflowConfiguration = (
  configuration: PersistedPackageConfiguration | null | undefined,
  packageId: number,
  packageName: string
): PackageConfiguration => {
  const baseConfiguration = createPackageCancellationWorkflowConfiguration(packageId, packageName);

  if (!configuration) {
    return baseConfiguration;
  }

  const normalizedPersonalizedOffer = configuration.personalizedOffer
    ? mergeOfferOptionConfiguration(baseConfiguration.cardConfigurations[0].recommendedOffer, configuration.personalizedOffer)
    : null;

  const mergedCardConfigurations = baseConfiguration.cardConfigurations.map((baseCard) => {
    const persistedCard = configuration.cardConfigurations?.find((card) => card.id === baseCard.id)
      || (normalizedPersonalizedOffer
        ? {
          id: baseCard.id,
          recommendedOffer: normalizedPersonalizedOffer,
          discountOffer: normalizedPersonalizedOffer,
        }
        : null);

    return mergeCardConfiguration(baseCard, persistedCard);
  });

  return {
    ...baseConfiguration,
    id: packageId,
    packageName,
    availableAddons: Array.isArray(configuration.availableAddons)
      ? [...configuration.availableAddons]
      : [...baseConfiguration.availableAddons],
    cardConfigurations: mergedCardConfigurations,
  };
};
